import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import UserAvatar from "../UserAvatar";
import {
  featuredTypeLabel,
  formatFeaturedShortDate,
  creatorProfilePath,
} from "../../utils/featuredDisplay";

export default function PastFeaturedStrip({ items = [], className = "" }) {
  const stripRef = useRef(null);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("ft-past-pill--visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    strip.querySelectorAll(".ft-past-pill").forEach((el, i) => {
      el.style.transitionDelay = `${i * 0.07}s`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  if (!items.length) {
    return (
      <p className="ft-past-empty">Past featured creators will appear here after the first nightly rotation.</p>
    );
  }

  return (
    <div className={`ft-past-strip ${className}`.trim()} ref={stripRef}>
      {items.map((item) => {
        const handle = item.handle || item.creatorHandle || item.username;
        const to = handle ? `/creator/${handle}` : creatorProfilePath({ handle, uid: item.creatorId });
        const name = item.displayName || item.creatorDisplayName || handle || "Creator";
        const avatar = item.avatar || item.avatarUrl || item.creatorAvatar || "";
        const dateLabel = formatFeaturedShortDate(item.lastFeatured || item.featuredAt);
        const typeLabel = featuredTypeLabel(item.type || item.featuredType);

        const inner = (
          <>
            <UserAvatar
              src={avatar}
              alt={name}
              name={name}
              className="ft-past-pill-av"
              imgClassName="ft-past-pill-av-img"
              initialsClassName="ft-past-pill-av-initials"
            />
            <div>
              <div className="ft-past-pill-name">{name}</div>
              <div className="ft-past-pill-date">
                {dateLabel}
                {dateLabel ? " · " : ""}
                {typeLabel}
              </div>
            </div>
          </>
        );

        if (!to) {
          return (
            <div key={item.id || `${name}-${dateLabel}`} className="ft-past-pill">
              {inner}
            </div>
          );
        }

        return (
          <Link key={item.id || `${name}-${dateLabel}`} to={to} className="ft-past-pill">
            {inner}
          </Link>
        );
      })}
    </div>
  );
}
